import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { ArrowLeft, ArrowRight, Check, ClipboardCheck, MapPin, CalendarDays, Loader2, Plus, Minus, Home, Euro, Eye, Settings, Droplets, FileText, ChevronDown, ChevronUp, AlertTriangle, DoorOpen, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

const STEPS = ['La tua Casa', 'Prenotazione', 'Conferma'];

export default function CheckGenerale() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);

  // Step 1
  const [numFinestre, setNumFinestre] = useState(0);
  const [numPorteFinestre, setNumPorteFinestre] = useState(0);
  const [numPortoneIngresso, setNumPortoneIngresso] = useState(0);
  const [descrizioneCasa, setDescrizioneCasa] = useState('');
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [codiceFiscale, setCodiceFiscale] = useState('');
  const [telefono, setTelefono] = useState('');
  const [emailCliente, setEmailCliente] = useState('');
  const [address, setAddress] = useState('');
  const [provincia, setProvincia] = useState('');
  const [faqOpen, setFaqOpen] = useState(null);

  const PROVINCE_DISPONIBILI = ['Brescia', 'Cremona', 'Mantova', 'Verona'];

  const totalSerramenti = numFinestre + numPorteFinestre + numPortoneIngresso;
  const needsDoubleSlot = totalSerramenti > 12;
  const prezzoCheck = needsDoubleSlot ? 360 : 180;

  // Step 2
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Distance
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);

  // Quote
  const [quoteId, setQuoteId] = useState(null);
  const [creatingQuote, setCreatingQuote] = useState(false);

  useEffect(() => {
    setQuoteId(null);
  }, [numFinestre, numPorteFinestre, numPortoneIngresso, descrizioneCasa, nome, cognome, codiceFiscale, telefono, emailCliente, address, provincia]);

  useEffect(() => {
    api.get('/pricing/config').then((r) => setConfig(r.data)).catch(() => toast.error('Errore configurazione'));
  }, []);

  const loadSlots = (prov) => {
    setLoadingSlots(true);
    setSlots([]);
    setSelectedDate(null);
    setSelectedSlot('');
    const params = new URLSearchParams({ service_type: 'check_generale', double: needsDoubleSlot ? 'true' : 'false' });
    if (prov) params.append('province', prov);
    api.get(`/appointments/slots?${params}`)
      .then((r) => setSlots(r.data))
      .catch(() => toast.error('Errore caricamento slot'))
      .finally(() => setLoadingSlots(false));
  };

  const goToStep2 = async () => {
    if (totalSerramenti === 0) { toast.error('Inserisci almeno un serramento'); return; }
    if (!nome || !cognome) { toast.error('Inserisci nome e cognome'); return; }
    if (!codiceFiscale.trim() || codiceFiscale.trim().length < 16) { toast.error('Inserisci il codice fiscale (16 caratteri)'); return; }
    if (!telefono.trim()) { toast.error('Inserisci il numero di telefono'); return; }
    if (!address.trim()) { toast.error('Inserisci l\'indirizzo'); return; }
    if (!provincia) { toast.error('Seleziona la provincia'); return; }
    let prov = provincia;
    if (address.trim()) {
      setLoadingDistance(true);
      try {
        const res = await api.post('/distance/calculate', { address });
        setDistanceInfo(res.data);
      } catch { setDistanceInfo(null); }
      finally { setLoadingDistance(false); }
    }
    loadSlots(prov);
    setStep(1);
  };

  const availableDates = slots.map((s) => parseISO(s.date));
  const getSlots = (date) => {
    if (!date) return [];
    const found = slots.find((s) => s.date === format(date, 'yyyy-MM-dd'));
    return found ? found.available_slots : [];
  };

  // Price calculation
  const surcharge = distanceInfo?.surcharge || 0;
  const totalPrice = prezzoCheck + surcharge;

  const SLOT_PAIRS = { '08:00-10:00': '08:00-12:00', '13:00-15:00': '13:00-17:00' };
  const slotLabel = (slot) => (needsDoubleSlot ? SLOT_PAIRS[slot] || slot : slot);

  const goToStep3 = async () => {
    if (quoteId) { setStep(2); return; }
    setCreatingQuote(true);
    try {
      const quoteRes = await api.post('/quotes', {
        service_type: 'check_generale',
        fixtures: [], fixture_details: [],
        num_finestre: numFinestre,
        num_porte: numPorteFinestre + numPortoneIngresso,
        descrizione_casa: `Finestre: ${numFinestre}, Porte-finestre: ${numPorteFinestre}, Portone ingresso: ${numPortoneIngresso}. ${descrizioneCasa}`.trim(),
        nome, cognome, telefono, email_cliente: emailCliente,
        codice_fiscale: codiceFiscale,
        address, zone: '', notes: needsDoubleSlot ? 'DOPPIO SLOT - Oltre 12 serramenti' : '',
        fonte: localStorage.getItem('regola_fonte') || '',
      });
      setQuoteId(quoteRes.data.quote_id);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingQuote(false);
    }
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-stone-50">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="check-generale-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => step > 0 ? setStep(step - 1) : navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <p className="text-sm font-bold text-slate-900">Check Generale</p>
            <p className="text-xs text-slate-400">Passo {step + 1} di {STEPS.length}</p>
          </div>
          <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold hidden">
            EUR {totalPrice}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`step-dot ${i < step ? 'completed' : i === step ? 'active' : 'pending'}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        {/* ── STEP 0: La tua Casa ── */}
        {step === 0 && (
          <div className="space-y-6" data-testid="step-casa">

            {/* Hero */}
            <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-8 relative overflow-hidden" data-testid="check-hero">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-amber-400/20" />
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-400 mb-2">Manutenzione serramenti a domicilio</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold leading-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Check Generale <span className="text-amber-400">Serramenti</span>
              </h1>
              <p className="text-sm text-slate-300 mt-2 max-w-lg">Un tecnico controlla, regola e lubrifica tutte le finestre e le porte di casa tua in una sola visita.</p>
              <div className="flex flex-wrap items-center gap-3 mt-5">
                <span className="bg-amber-400 text-slate-900 text-sm font-extrabold px-4 py-1.5 rounded-full" data-testid="hero-price">da EUR 180</span>
                <span className="text-xs text-slate-400">Pagamento al tecnico a fine intervento</span>
              </div>
            </div>

            {/* Cosa include il Check */}
            <div>
              <h2 className="text-base md:text-lg font-extrabold text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center"><ClipboardCheck className="w-4 h-4" /></span>
                Cosa include
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: Eye, title: 'Ispezione Completa', desc: 'Controlliamo ogni serramento: finestre, porte-finestre, persiane, tapparelle e porte.', c: 'bg-blue-50 border-blue-200', ic: 'bg-blue-600', t: 'text-blue-800' },
                  { icon: Settings, title: 'Regolazione Meccanismi', desc: 'Regoliamo cerniere, maniglie, chiusure multipunto e meccanismi di apertura.', c: 'bg-amber-50 border-amber-200', ic: 'bg-amber-500', t: 'text-amber-800' },
                  { icon: Droplets, title: 'Lubrificazione Professionale', desc: 'Oliamo tutti i punti di movimento con prodotti specifici per durata e scorrevolezza.', c: 'bg-emerald-50 border-emerald-200', ic: 'bg-emerald-600', t: 'text-emerald-800' },
                  { icon: FileText, title: 'Report Dettagliato', desc: 'Registriamo lo stato di ogni serramento e consigliamo eventuali interventi futuri.', c: 'bg-rose-50 border-rose-200', ic: 'bg-rose-500', t: 'text-rose-800' },
                ].map((s, i) => (
                  <div key={i} className={`flex items-start gap-3 rounded-xl border p-4 ${s.c} transition-transform duration-200 hover:-translate-y-0.5`} data-testid={`include-${i}`}>
                    <span className={`w-9 h-9 rounded-lg ${s.ic} text-white flex items-center justify-center flex-shrink-0`}><s.icon className="w-4 h-4" /></span>
                    <div>
                      <p className={`text-sm font-extrabold ${s.t}`}>{s.title}</p>
                      <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="text-xs text-slate-400 mr-1">Zone servite:</span>
                {['Brescia', 'Verona', 'Mantova', 'Cremona'].map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 bg-white text-slate-600 text-xs px-2.5 py-1 rounded-full border border-slate-200">
                    <MapPin className="w-3 h-3 text-blue-400" />{p}
                  </span>
                ))}
              </div>
            </div>

            {/* Contatore serramenti */}
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-1">Quanti serramenti hai in casa?</p>
              <p className="text-xs text-slate-500 mb-3">Fino a 12 serramenti: EUR 180. Oltre 12: 2 fasce orarie, EUR 360.</p>
              <Card className="border-0 shadow-sm rounded-xl">
                <CardContent className="p-5 space-y-4">
                  {[
                    { label: 'Finestre', key: 'finestre', value: numFinestre, set: setNumFinestre, hint: 'Finestre a battente, scorrevoli, vasistas...' },
                    { label: 'Porte-finestre', key: 'portefinestre', value: numPorteFinestre, set: setNumPorteFinestre, hint: 'Porte che danno su balcone o terrazzo' },
                    { label: 'Portone d\'ingresso', key: 'portone', value: numPortoneIngresso, set: setNumPortoneIngresso, hint: 'Porta blindata o portone principale' },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">{item.label}</p>
                        <p className="text-xs text-slate-400">{item.hint}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => item.set(Math.max(0, item.value - 1))} data-testid={`${item.key}-minus`}>
                          <Minus className="w-3.5 h-3.5" />
                        </Button>
                        <span className="w-8 text-center font-bold text-lg" data-testid={`${item.key}-count`}>{item.value}</span>
                        <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => item.set(item.value + 1)} data-testid={`${item.key}-plus`}>
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Prezzo dinamico */}
            {totalSerramenti > 0 && (
              <Card className={`border-0 shadow-sm rounded-xl ${needsDoubleSlot ? 'bg-amber-50 ring-1 ring-amber-200' : 'bg-emerald-50 ring-1 ring-emerald-200'}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        {totalSerramenti} serrament{totalSerramenti === 1 ? 'o' : 'i'} total{totalSerramenti === 1 ? 'e' : 'i'}
                      </p>
                      {needsDoubleSlot ? (
                        <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Oltre 12 serramenti: servono 2 fasce orarie
                        </p>
                      ) : (
                        <p className="text-xs text-emerald-700 mt-1">Durata intervento: circa 1h 30min (1 fascia oraria)</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        EUR {prezzoCheck}
                      </p>
                      {needsDoubleSlot && (
                        <p className="text-xs text-amber-600 font-medium">2 slot x EUR 180</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* FAQ */}
            <div>
              <h2 className="text-base md:text-lg font-extrabold text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center font-black text-sm">?</span>
                Domande frequenti
              </h2>
              <div className="space-y-2">
                {[
                  {
                    q: 'Come funziona il Check Generale?',
                    a: 'Prenoti online scegliendo data e orario. Il nostro tecnico viene a casa tua, ispeziona tutti i serramenti, li regola, li lubrifica e ti consegna un report dettagliato. Paghi direttamente al tecnico a fine intervento.'
                  },
                  {
                    q: 'Perché è importante fare manutenzione ai serramenti?',
                    a: 'Senza manutenzione regolare, i problemi piccoli diventano gravi e costosi. Ad esempio: una porta d\'ingresso non regolata può portare alla rottura della serratura. Una porta-finestra che "striscia" può graffiare il parquet in modo permanente. Guarnizioni secche causano spifferi, umidità e bollette più alte.'
                  },
                  {
                    q: 'Cosa succede se il tecnico trova un problema grave?',
                    a: 'Se durante il check emergono problemi gravi o pezzi da sostituire, il tecnico ti informa subito e concordiamo un secondo intervento con preventivo dedicato. Non facciamo mai lavori extra senza il tuo consenso.'
                  },
                  {
                    q: 'Quanto dura l\'intervento?',
                    a: 'Per case fino a 12 serramenti, circa 1 ora e 30 minuti (1 fascia oraria). Per case con più di 12 serramenti, servono 2 fasce orarie consecutive (circa 3 ore).'
                  },
                  {
                    q: 'Quali materiali di serramenti trattate?',
                    a: 'Lavoriamo su tutti i tipi di serramenti: alluminio, PVC, legno, legno-alluminio, acciaio. Finestre, porte-finestre, persiane, tapparelle e portoni blindati.'
                  },
                ].map((faq, i) => (
                  <Card key={i} className={`border-0 shadow-sm rounded-xl overflow-hidden cursor-pointer border-l-4 ${faqOpen === i ? 'border-l-violet-600 bg-violet-50/40' : 'border-l-transparent'}`} onClick={() => setFaqOpen(faqOpen === i ? null : i)} data-testid={`faq-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm font-bold flex-1 pr-2 ${faqOpen === i ? 'text-violet-700' : 'text-slate-800'}`}>{faq.q}</p>
                        {faqOpen === i ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                      </div>
                      {faqOpen === i && (
                        <p className="text-sm text-slate-600 leading-relaxed mt-3 pt-3 border-t border-violet-100">{faq.a}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Descrizione casa */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1 block">
                <Home className="w-4 h-4 inline mr-1" />
                Descrivi la tua casa (opzionale)
              </Label>
              <Textarea value={descrizioneCasa} onChange={(e) => setDescrizioneCasa(e.target.value)}
                placeholder="Es: Appartamento al 3 piano, villa su 2 livelli, serramenti in legno/PVC/alluminio..."
                className="rounded-xl" rows={3} data-testid="descrizione-casa" />
            </div>

            <Separator />
            <h2 className="text-base md:text-lg font-extrabold text-slate-900 flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><Check className="w-4 h-4" /></span>
              I tuoi dati
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Nome *</Label>
                <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Il tuo nome" className="rounded-xl" data-testid="nome-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Cognome *</Label>
                <Input value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Il tuo cognome" className="rounded-xl" data-testid="cognome-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs text-slate-500 mb-1 block">Codice Fiscale *</Label>
                <Input value={codiceFiscale} onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())} placeholder="RSSMRA85M01H501Z" className="rounded-xl font-mono" maxLength={16} data-testid="cf-input" />
                <p className="text-xs text-slate-400 mt-1">Serve per effettuare la fattura</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Telefono *</Label>
                <Input value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="+39 ..." type="tel" className="rounded-xl" data-testid="telefono-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Email</Label>
                <Input value={emailCliente} onChange={(e) => setEmailCliente(e.target.value)} placeholder="email@esempio.it" type="email" className="rounded-xl" data-testid="email-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Indirizzo *</Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Via, numero, città" className="rounded-xl" data-testid="address-input" />
                <p className="text-xs text-slate-400 mt-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Serve per calcolare la distanza dalla nostra sede
                </p>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Provincia *</Label>
                <select
                  value={provincia}
                  onChange={(e) => setProvincia(e.target.value)}
                  className="flex h-10 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="provincia-select"
                >
                  <option value="">Seleziona provincia</option>
                  {PROVINCE_DISPONIBILI.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8" onClick={goToStep2} data-testid="next-step-btn">
                {loadingDistance ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 1: Prenotazione ── */}
        {step === 1 && (
          <div className="space-y-6" data-testid="step-prenotazione">
            {distanceInfo?.distance_km && (
              <Card className="border border-emerald-200 bg-emerald-50/50 rounded-xl">
                <CardContent className="p-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <div className="text-sm">
                    <span className="font-semibold text-emerald-800">Distanza: {distanceInfo.distance_km} km</span>
                    <span className="text-slate-500 ml-2">da {config.base_location}</span>
                    {surcharge > 0 && (
                      <span className="text-emerald-700 ml-2 font-semibold">(+EUR {surcharge} rimborso km A/R)</span>
                    )}
                    {distanceInfo.province && (
                      <span className="block mt-1 text-blue-600 font-medium">Provincia: {distanceInfo.province}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {distanceInfo?.province && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
                <strong>Come funziona:</strong> Il calendario mostra solo i giorni in cui il tecnico opera nella tua provincia ({distanceInfo.province}).
                Disponibilità dal Lunedì al Venerdì.
              </div>
            )}

            {/* Price summary */}
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Check Generale ({totalSerramenti} serrament{totalSerramenti === 1 ? 'o' : 'i'}{needsDoubleSlot ? ', 2 slot' : ''})</span>
                  <span className="font-semibold text-slate-900">EUR {prezzoCheck}</span>
                </div>
                {surcharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Rimborso km A/R ({distanceInfo?.distance_km} km x 2 x EUR 0,50)</span>
                    <span className="font-semibold text-orange-600">+EUR {surcharge}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-sm">
                  <span className="font-bold text-slate-900">Totale Check Generale</span>
                  <span className="font-extrabold text-lg text-slate-900">EUR {totalPrice}</span>
                </div>
              </CardContent>
            </Card>

            <p className="text-sm font-semibold text-slate-700">Scegli data e orario</p>

            {loadingSlots ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => { setSelectedDate(d); setSelectedSlot(''); }}
                      locale={it}
                      disabled={(date) => {
                        if (date < new Date()) return true;
                        return !availableDates.some((ad) => isSameDay(ad, date));
                      }}
                      modifiers={{ available: availableDates }}
                      modifiersStyles={{ available: { fontWeight: 'bold', color: '#F97316' } }}
                      className="w-full"
                      data-testid="calendar-check"
                    />
                  </CardContent>
                </Card>
                <div>
                  {selectedDate ? (
                    <Card className="border-0 shadow-sm rounded-xl">
                      <CardContent className="p-5 space-y-2">
                        <p className="text-sm font-bold text-slate-900 flex items-center gap-2 mb-3">
                          <CalendarDays className="w-4 h-4 text-blue-600" />
                          {format(selectedDate, 'EEEE d MMMM', { locale: it })}
                        </p>
                        {needsDoubleSlot && (
                          <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2 mb-2 flex items-center gap-1" data-testid="double-slot-warning">
                            <AlertTriangle className="w-3.5 h-3.5" /> Oltre 12 serramenti: verranno bloccate 2 fasce consecutive
                          </p>
                        )}
                        {getSlots(selectedDate).map((slot) => (
                          <div
                            key={slot}
                            className={`slot-available ${selectedSlot === slot ? 'selected' : ''}`}
                            onClick={() => setSelectedSlot(slot)}
                            data-testid={`slot-${slot}`}
                          >
                            <span className="font-medium text-sm">{slotLabel(slot)}</span>
                            <span className="text-xs opacity-70">{needsDoubleSlot ? '2 fasce (~3h)' : '1h 30min'}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="border-0 shadow-sm rounded-xl">
                      <CardContent className="p-8 text-center">
                        <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">Seleziona una data</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" className="rounded-full" onClick={() => setStep(0)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
                disabled={!selectedDate || !selectedSlot || creatingQuote}
                onClick={goToStep3} data-testid="next-step-btn">
                {creatingQuote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Avanti <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Riepilogo e Garanzia ── */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-pagamento">
            {/* Price breakdown */}
            <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
              <div className="bg-slate-900 text-white p-6">
                <p className="text-sm text-slate-400 mb-1">Check Generale Serramenti</p>
                <p className="text-3xl font-extrabold" data-testid="check-price">EUR {totalPrice}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {totalSerramenti} serrament{totalSerramenti === 1 ? 'o' : 'i'} | {needsDoubleSlot ? '2 fasce orarie (~3h)' : 'Durata ~1h 30min'}
                </p>
              </div>
              <CardContent className="p-5 space-y-2">
                {surcharge > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Rimborso km A/R ({distanceInfo?.distance_km} km x 2 x EUR 0,50)</span>
                    <span className="font-semibold text-orange-600">+EUR {surcharge}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Appointment summary */}
            <Card className="border-0 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 font-semibold">Appuntamento</p>
                <p className="text-sm font-semibold text-slate-900">
                  {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })} - {slotLabel(selectedSlot)}{needsDoubleSlot ? ' (doppio slot)' : ''}
                </p>
                <Separator className="my-3" />
                <p className="text-sm text-slate-700">{nome} {cognome}</p>
                {address && <p className="text-xs text-slate-500">{address}</p>}
              </CardContent>
            </Card>

            {/* Payment - Only pay at technician */}
            <Card className="border-0 shadow-sm rounded-xl bg-emerald-50">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Euro className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Pagamento al tecnico</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Potrai pagare direttamente al tecnico una volta finito l'intervento. Accettiamo contanti, POS e bonifico.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full py-6 text-base font-semibold"
              onClick={async () => {
                try {
                  await api.post('/appointments', {
                    quote_id: quoteId,
                    date: format(selectedDate, 'yyyy-MM-dd'),
                    time_slot: selectedSlot,
                    zone: '',
                    province: provincia,
                    notes: `Check Generale - ${totalSerramenti} serramenti${needsDoubleSlot ? ' (DOPPIO SLOT)' : ''}`,
                  });
                  toast.success('Prenotazione confermata!');
                  navigate('/');
                } catch (err) { toast.error(err.response?.data?.detail || 'Errore conferma'); }
              }}
              data-testid="confirm-booking-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Conferma Prenotazione
            </Button>

            <div className="pt-2">
              <Button variant="outline" className="rounded-full w-full" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Modifica
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
